// src/pages/yachts/special-offers/parseApiError.js
// Turns a 422 from POST/PUT /charter-special-offers into something the form can show:
// a readable message for the alert, the conflicting offer id for overlaps, and the
// redux-form field the message is about (as SubmissionError input) so it also renders
// inline. The API sends one string per failure, always prefixed with the field name
// (see docs/charter-special-offers-frontend.md §7).

import { OFFER_TYPE_LABELS, AUDIENCE_LABELS } from './validate';

const GENERIC = 'Save failed';

// Form field name → label shown in the form.
const FIELD_LABELS = {
    name: 'Name',
    description: 'Description',
    offer_type: 'Offer type',
    discount_percent: 'Discount %',
    discount_amount: 'Flat amount',
    discount_amount_currency: 'Currency',
    free_days: 'Free days',
    date_from: 'Date from',
    date_to: 'Date to',
    publish_start_date: 'Publish from',
    publish_end_date: 'Publish to',
    booking_window_start: 'Booking window start',
    booking_window_end: 'Booking window end',
    min_charter_days: 'Min charter days',
    max_charter_days: 'Max charter days',
    audience: 'Audience',
    access_code: 'Access code',
    allowed_broker_ids: 'Allowed brokers',
    location_ids: 'Locations',
    priority: 'Priority',
    is_active: 'Active',
    allow_partial_application: 'Partial application'
};

const TIER_FIELD_LABELS = {
    min_duration_days: 'Min days',
    max_duration_days: 'Max days',
    discount_percent: 'Discount %',
    package_price: 'Package price',
    package_currency: 'Currency'
};

const LABELS = {
    ...FIELD_LABELS,
    ...TIER_FIELD_LABELS,
    ...OFFER_TYPE_LABELS,
    ...AUDIENCE_LABELS,
    yacht_id: 'Yacht'
};

const TOKEN = new RegExp(`\\b(${Object.keys(LABELS).join('|')})\\b`, 'g');

// Also used for the post-save warnings, which name fields the same way.
export function humanize(message) {
    return message
        .replace(TOKEN, (_, key) => LABELS[key])
        .replace(/\btier (\d+)/g, 'Tier $1');
}

function tierRow(index, field, message) {
    const rows = [];
    rows[index] = { [field]: message };
    return { tiers: rows };
}

// Which form field a raw API message is about, as SubmissionError input.
function fieldErrorsFor(raw, message) {
    let m = raw.match(/^tier (\d+) and tier (\d+) cover overlapping/);
    if (m) return tierRow(Number(m[2]) - 1, 'min_duration_days', message);

    m = raw.match(/^tier (\d+) (\w+)/);
    if (m && TIER_FIELD_LABELS[m[2]])
        return tierRow(Number(m[1]) - 1, m[2], message);

    if (/requires at least one tier|must not define tiers/.test(raw)) {
        return { tiers: { _error: message } };
    }
    if (/^Unknown broker ids/.test(raw)) return { allowed_broker_ids: message };
    if (/^Invalid location|^Locations .* have no rates/.test(raw)) {
        return { location_ids: message };
    }

    // The field named first is the one the rule is about.
    const field = Object.keys(FIELD_LABELS)
        .map((f) => ({ f, at: raw.search(new RegExp(`\\b${f}\\b`)) }))
        .filter((x) => x.at >= 0)
        .sort((a, b) => a.at - b.at)
        .map((x) => x.f)[0];
    return field ? { [field]: message } : null;
}

export default function parseApiError(error) {
    const data = error && error.response && error.response.data;
    const raw = data && typeof data.message === 'string' ? data.message : '';
    if (!raw)
        return { message: GENERIC, conflictOfferId: null, fieldErrors: null };

    const overlap = raw.match(/Offer dates overlap existing offer #(\d+)/);
    if (overlap) {
        return {
            message: raw,
            conflictOfferId: Number(overlap[1]),
            fieldErrors: null
        };
    }

    const message = humanize(raw);
    // Inline, keep the rule itself; the alert above the form carries the explanation.
    const inline = message.split(' — ')[0];
    return {
        message,
        conflictOfferId: null,
        fieldErrors: fieldErrorsFor(raw, inline)
    };
}
